import { motion } from "framer-motion";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { Activity, Sparkles, Calendar, MessageCircle, Check } from "lucide-react";
import PortfolioHealthPreview from "./previews/PortfolioHealthPreview";
import AiAnalysisPreview from "./previews/AiAnalysisPreview";
import DividendCalendarPreview from "./previews/DividendCalendarPreview";

const FeatureSpotlight = () => {
  const { t } = useTranslation();

  const blocks = [
    {
      eyebrow: t("spotlight.health.eyebrow"),
      title: t("spotlight.health.title"),
      desc: t("spotlight.health.desc"),
      bullets: [
        t("spotlight.health.b1"),
        t("spotlight.health.b2"),
        t("spotlight.health.b3"),
      ],
      Icon: Activity,
      visual: <PortfolioHealthPreview />,
      reverse: false,
      accent: "hsl(var(--primary))",
      tint: "hsl(var(--primary) / 0.10)",
    },
    {
      eyebrow: t("spotlight.ai.eyebrow"),
      title: t("spotlight.ai.title"),
      desc: t("spotlight.ai.desc"),
      bullets: [
        t("spotlight.ai.b1"),
        t("spotlight.ai.b2"),
        t("spotlight.ai.b3"),
      ],
      Icon: Sparkles,
      visual: <AiAnalysisPreview />,
      reverse: true,
      accent: "hsl(var(--brand-amber))",
      tint: "hsl(var(--brand-amber) / 0.14)",
    },
    {
      eyebrow: t("spotlight.dividends.eyebrow"),
      title: t("spotlight.dividends.title"),
      desc: t("spotlight.dividends.desc"),
      bullets: [
        t("spotlight.dividends.b1"),
        t("spotlight.dividends.b2"),
        t("spotlight.dividends.b3"),
      ],
      Icon: Calendar,
      visual: <DividendCalendarPreview />,
      reverse: false,
      accent: "hsl(var(--brand-clay))",
      tint: "hsl(var(--brand-clay) / 0.14)",
    },
  ];

  return (
    <section id="features" className="py-24 lg:py-32 relative overflow-hidden">
      <div className="container mx-auto px-6 sm:px-8 lg:px-12 space-y-28 lg:space-y-40">
        {blocks.map((b, i) => (
          <div
            key={b.title}
            className={`grid lg:grid-cols-2 gap-12 lg:gap-20 items-center ${
              b.reverse ? "lg:[&>div:first-child]:order-2" : ""
            }`}
          >
            <motion.div
              initial={{ opacity: 0, x: b.reverse ? 40 : -40 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true, margin: "-100px" }}
              transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
            >
              <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full mb-5" style={{ background: b.tint }}>
                <b.Icon className="w-4 h-4" style={{ color: b.accent }} />
                <span className="text-xs font-bold uppercase tracking-widest" style={{ color: b.accent }}>
                  {b.eyebrow}
                </span>
              </div>
              <h3 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold tracking-tight mb-5 text-balance">
                {b.title}
              </h3>
              <p className="text-lg text-muted-foreground leading-relaxed mb-7 max-w-xl">
                {b.desc}
                {i === 1 && <> <Link to="/terms#fair-use" className="text-primary hover:underline">{t("faq.fairUse")}</Link></>}
              </p>
              <ul className="space-y-3">
                {b.bullets.map((bullet) => (
                  <li key={bullet} className="flex items-start gap-3">
                    <div className="w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5" style={{ background: b.tint }}>
                      <Check className="w-3 h-3" style={{ color: b.accent }} strokeWidth={3} />
                    </div>
                    <span className="text-foreground/80">{bullet}</span>
                  </li>
                ))}
              </ul>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-100px" }}
              transition={{ duration: 0.8, delay: 0.15, ease: [0.16, 1, 0.3, 1] }}
              className="flex justify-center"
            >
              {b.visual}
            </motion.div>
          </div>
        ))}

        {/* AI Chat coming soon teaser */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7 }}
          className="relative max-w-4xl mx-auto rounded-3xl bg-band-forest text-[hsl(var(--brand-forest-deep-foreground))] p-10 lg:p-14 overflow-hidden"
        >
          <div className="absolute -top-32 -right-32 w-80 h-80 rounded-full blur-[120px]" style={{ background: "hsl(var(--brand-amber) / 0.30)" }} />
          <div className="absolute -bottom-32 -left-32 w-80 h-80 rounded-full blur-[120px]" style={{ background: "hsl(var(--accent) / 0.20)" }} />
          <div className="relative grid md:grid-cols-2 gap-10 items-center">
            <div>
              <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full mb-5" style={{ background: "hsl(var(--brand-amber) / 0.18)" }}>
                <MessageCircle className="w-4 h-4" style={{ color: "hsl(var(--brand-amber))" }} />
                <span className="text-xs font-bold uppercase tracking-widest" style={{ color: "hsl(var(--brand-amber))" }}>
                  {t("spotlight.chat.eyebrow")}
                </span>
              </div>
              <h3 className="text-3xl lg:text-4xl font-extrabold tracking-tight mb-4 text-balance">
                {t("spotlight.chat.title")}
              </h3>
              <p className="text-[hsl(var(--accent))]/85 leading-relaxed">
                {t("spotlight.chat.desc")}
              </p>
            </div>

            <div className="space-y-3">
              {[
                { from: "you", text: t("spotlight.chat.q1") },
                { from: "ai", text: t("spotlight.chat.a1") },
                { from: "you", text: t("spotlight.chat.q2") },
              ].map((m, i) => (
                <motion.div
                  key={i}
                  initial={{ opacity: 0, y: 12 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: 0.2 + i * 0.15 }}
                  className={`max-w-[85%] px-4 py-2.5 rounded-2xl text-sm ${
                    m.from === "you"
                      ? "bg-white/10 text-[hsl(var(--brand-forest-deep-foreground))] ml-auto rounded-br-md border border-white/10"
                      : "bg-accent text-[hsl(var(--brand-forest-deep))] rounded-bl-md"
                  }`}
                >
                  {m.text}
                </motion.div>
              ))}
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default FeatureSpotlight;
